import React from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { DataGrid } from "@mui/x-data-grid";
import MainCard from "components/MainCard";
import CheckBarcodeForStockController from "pages/Controller/CheckBarcodeForStockController";

function CheckBarcodeForStock() {
  const {
    barcodeText,
    materialDescription,
    isMaterialDescriptionMode,
    handleBarcodeChange,
    handleMaterialDescriptionChange,
    handleBarcodeKeyDown,
    handleBarcodePaste,
    handleFileUpload,
    handleInputModeChange,
    handleClearButtonClick,
    handleSubmit,
    handleDownloadCSV,
    handleMaterialDescriptionKeyDown,
    resultData,
    cloading,
    columns,
    fileInputRef,
    errorMessage,
    columnsGarment,
    rowsGarment,
    layoutType,
  } = CheckBarcodeForStockController();

  const darkGridSx = {
    border: "1px solid #d9d9d9",
    color: "#111",
    backgroundColor: "#fff",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      color: "#111",
      borderBottom: "1px solid #d9d9d9"
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      whiteSpace: "normal",
      lineHeight: 1.2,
      textAlign: "center"
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #e5e5e5",
      alignItems: "center",
      lineHeight: 1.25,
      py: 0.75,
      whiteSpace: "normal !important",
      wordBreak: "break-word"
    },
    "& .MuiDataGrid-cellContent": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip"
    },
    "& .MuiChip-label": {
      whiteSpace: "nowrap",
      overflow: "visible",
      textOverflow: "clip"
    },
    "& .MuiDataGrid-columnHeader": {
      whiteSpace: "normal"
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "#dfeaff !important"
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: "#fff"
    },
    "& .MuiSvgIcon-root": {
      color: "#111"
    }
  };


  
  return (
    <Box sx={{ minHeight: "calc(100vh - 80px)", px: { xs: 2, md: 3 }, pt: { xs: 7, md: 9 }, pb: { xs: 2, md: 3 }, backgroundColor: "#000" }}>
      <style>
        {`
          .barcode-stock-page .strip-even { background-color: #d8d8d8ff !important; }
          .barcode-stock-page .strip-odd { background-color: #fff !important; }
          .barcode-stock-page .MuiDataGrid-row:hover { background-color: #dfeaff !important; }
        `}
      </style>

      <MainCard
        className="barcode-stock-page"
        contentSX={{ p: 0 }}
        sx={{
          backgroundColor: "#0b0b0b",
          borderColor: "#242424",
          color: "#fff",
          "& .MuiCardHeader-root": {
            backgroundColor: "#000",
            borderBottom: "1px solid #242424"
          },
          "& .MuiCardHeader-title": {
            color: "#fff",
            fontWeight: 700
          }
        }}
      >
        <Card elevation={0} sx={{ p: 2, boxShadow: "none", backgroundColor: "#0b0b0b", color: "#fff" }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1.5} sx={{ minHeight: 40 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={isMaterialDescriptionMode ? "material" : "barcode"}
                  onChange={handleInputModeChange}
                  color="primary"
                  sx={{
                    "& .MuiToggleButton-root": {
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 700,
                      textTransform: "none",
                      borderColor: "#3a3a3a",
                      color: "#d8d8d8",
                      "&.Mui-selected": {
                        backgroundColor: "#d32f2f",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#b71c1c" }
                      },
                      "&:hover": {
                        backgroundColor: "#1c1c1c"
                      }
                    }
                  }}
                >
                  <ToggleButton value="barcode">
                    <QrCodeScannerIcon fontSize="small" />
                    Barcode
                  </ToggleButton>
                  <ToggleButton value="material">
                    <DescriptionIcon fontSize="small" />
                    Quality Name
                  </ToggleButton>
                </ToggleButtonGroup>

                <Typography component="label" variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {isMaterialDescriptionMode ? "Search by material description" : "Enter, paste, or upload barcodes"}
                </Typography>
              </Box>

              {!isMaterialDescriptionMode ? (
                <Box sx={{ position: "relative", width: { xs: "100%", md: "93%" }, mt: 1 }}>
                  <textarea
                    value={barcodeText}
                    onChange={handleBarcodeChange}
                    onKeyDown={handleBarcodeKeyDown}
                    onPaste={handleBarcodePaste}
                    placeholder="Enter or paste barcodes here..."
                    style={{
                      width: "100%",
                      minHeight: "200px",
                      maxHeight: "300px",
                      padding: "12px 48px 12px 12px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      resize: "vertical",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      overflowY: "auto",
                      outline: "none",
                      boxSizing: "border-box",
                      backgroundColor: "#fff",
                      color: "#111",
                      caretColor: "#111",
                    }}
                  />

                  <Tooltip title="Upload text file">
                    <IconButton
                      color="primary"
                      component="label"
                      sx={{
                        position: { xs: "static", md: "absolute" },
                        right: -60,
                        bottom: 10,
                        mt: { xs: 1, md: 0 },
                        border: "1px solid #3a3a3a",
                        borderRadius: "10px",
                        p: 1.5,
                        backgroundColor: "#fff",
                        color: "#111",
                        "&:hover": { backgroundColor: "#f0f0f0" },
                      }}
                    >
                      <CloudUploadIcon />
                      <input type="file" hidden ref={fileInputRef} onChange={handleFileUpload} accept=".txt" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box sx={{ width: { xs: "100%", md: "40%" }, minWidth: 260, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={materialDescription}
                    onChange={handleMaterialDescriptionChange}
                    onKeyDown={handleMaterialDescriptionKeyDown}
                    placeholder="Enter material description"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        minHeight: 40,
                        color: "#111",
                        "& fieldset": { borderColor: "#ccc" },
                        "&:hover fieldset": { borderColor: "#999" },
                        "&.Mui-focused fieldset": { borderColor: "#d32f2f" }
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#777",
                        opacity: 1
                      }
                    }}
                  />
                </Box>
              )}
            </Box>

            {errorMessage && (
              <Typography variant="caption" sx={{ textAlign: "left", color: "#ff8a80" }}>
                {errorMessage}
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={handleClearButtonClick}
                disabled={cloading}
                sx={{
                  width: 120,
                  borderColor: "#3a3a3a",
                  color: "#fff",
                  "&:hover": { borderColor: "#777", backgroundColor: "#1c1c1c" }
                }}
              >
                Clear
              </Button>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={cloading} sx={{ width: 140 }}>
                {cloading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {resultData.length > 0 && (
              <Box mb={1} display="flex" justifyContent="flex-end">
                <Tooltip title="Download CSV">
                  <IconButton
                    color="primary"
                    onClick={handleDownloadCSV}
                    sx={{ border: "1px solid #3a3a3a", borderRadius: "10px", p: 1.5,color: "#111", backgroundColor: "#fff", "&:hover": { backgroundColor: "#f0f0f0" } }}
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {cloading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 500, width: "100%" }}>
                {layoutType === "garment" && (
                  <DataGrid
                    rows={rowsGarment}
                    columns={columnsGarment}
                    getRowHeight={() => "auto"}
                    columnHeaderHeight={48}
                    pagination={false}
                    hideFooter
                    sx={darkGridSx}
                    getRowClassName={(params) => (params.row.groupIndex % 2 === 0 ? "strip-even" : "strip-odd")}
                  />
                )}

                {(layoutType === "fabric" || layoutType == null) && (
                  <DataGrid
                    rows={resultData}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    columnHeaderHeight={48}
                    pagination={false}
                    hideFooter
                    sx={darkGridSx}
                    getRowClassName={(params) => (params.row.groupIndex % 2 === 0 ? "strip-even" : "strip-odd")}
                  />
                )}
              </Box>
            )}
          </Box>
        </Card>
      </MainCard>
    </Box>
  );
}

export default CheckBarcodeForStock;
